/* eslint-disable @typescript-eslint/no-explicit-any */
import clsx from 'clsx';
import ChatCheckbox from 'core/chatbot/components/atoms/ChatCheckbox';
import React from 'react';
import { Icon, InputCheckbox } from 'ui';

import { SelectCustomizable } from '../legacyAtoms';

import './_multi-select-with-checkbox.scss';

type OptionType = {
  value: string;
  label: string;
};

/** react-select change metadata, forwarded so consumers can tell a clear-✕ from a deselect-all. */
export type MultiSelectChangeMeta = { action?: string };

type MultiSelectWithCheckboxProps = {
  options: OptionType[];
  value: OptionType[];
  onChange: (selected: OptionType[] | null, meta?: MultiSelectChangeMeta) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  maxVisibleValues?: number;
  name?: string;
  label?: string | null;
  required?: boolean;
  // --- Additive props (backward-compatible) ---
  /** When set, a synthetic keyboard-reachable "select all" option is prepended. */
  selectAllLabel?: string;
  /** Trigger label rendered (instead of badges) when every option is selected. */
  allSelectedLabel?: string;
  /** Forwarded to the underlying select as `aria-label`. */
  ariaLabel?: string;
};

/** Sentinel value for the synthetic "select all" option (never leaks to the consumer). */
const SELECT_ALL_VALUE = '__all__';

// Custom MultiValue to limit visible badges and show "+N" overflow
const LimitedMultiValue = (props: any) => {
  const { index, getValue, selectProps, data, removeProps } = props;
  const maxVisible = selectProps.maxVisibleValues ?? 1;
  const allValues = getValue();

  if (index < maxVisible) {
    return (
      <div className="multi-select-checkbox__badge">
        <span className="multi-select-checkbox__badge-text">{data.label}</span>
        <button
          type="button"
          className="multi-select-checkbox__badge-remove"
          onClick={(e) => {
            e.stopPropagation();
            removeProps.onClick(e);
          }}
          aria-label={`Remove ${data.label}`}
        >
          ×
        </button>
      </div>
    );
  }

  if (index === maxVisible) {
    const overflowCount = allValues.length - maxVisible;
    return (
      <div className="multi-select-checkbox__badge multi-select-checkbox__badge--count">
        <span className="multi-select-checkbox__badge-text">+{overflowCount}</span>
      </div>
    );
  }

  return null;
};

// Custom Option using existing ChatCheckbox component. The synthetic "select all"
// option renders via the ui InputCheckbox so it can show the indeterminate state
// (partial selection); real options keep the existing ChatCheckbox look.
// Overriding the DS AccessibleOption means row semantics are on us: react-select v3
// does not put role="option"/aria-selected in innerProps.
const CheckboxOption = (props: any) => {
  const { isSelected, label, innerRef, innerProps, data, selectProps } = props;
  const isSelectAll = data?.value === SELECT_ALL_VALUE;

  if (isSelectAll) {
    const realOptions: OptionType[] = (selectProps.options ?? []).filter((opt: OptionType) => opt.value !== SELECT_ALL_VALUE);
    const selectedCount: number = selectProps.value?.length ?? 0;
    const isAllSelected = realOptions.length > 0 && selectedCount === realOptions.length;
    const isPartial = selectedCount > 0 && !isAllSelected;

    return (
      <div
        ref={innerRef}
        {...innerProps}
        role="option"
        aria-selected={isAllSelected}
        className={clsx('multi-select-checkbox__option', 'multi-select-checkbox__option--select-all', {
          'multi-select-checkbox__option--selected': isAllSelected,
        })}
      >
        <InputCheckbox checked={isAllSelected} indeterminate={isPartial} label={label} onChange={() => {}} variant="square" />
      </div>
    );
  }

  return (
    <div
      ref={innerRef}
      {...innerProps}
      role="option"
      aria-selected={isSelected}
      className={clsx('multi-select-checkbox__option', { 'multi-select-checkbox__option--selected': isSelected })}
    >
      <ChatCheckbox checked={isSelected} label={label} onChange={() => {}} />
    </div>
  );
};

// Custom DropdownIndicator to use Icon component instead of CSS background SVG
const CustomDropdownIndicator = (props: any) => {
  const { innerProps } = props;
  return (
    <div className="select-customizable__indicator" {...innerProps}>
      <Icon name="chevron-down" size="sm" color="grey-01" />
    </div>
  );
};

// Custom ValueContainer: when every option is selected and `allSelectedLabel` is
// set, render the single summary label instead of per-option badges.
const AllSelectedValueContainer = (props: any) => {
  const { children, selectProps } = props;
  const realOptions: OptionType[] = (selectProps.options ?? []).filter((opt: OptionType) => opt.value !== SELECT_ALL_VALUE);
  const selectedCount: number = selectProps.value?.length ?? 0;
  const isAllSelected = realOptions.length > 0 && selectedCount === realOptions.length;

  if (isAllSelected && selectProps.allSelectedLabel) {
    // children = [value nodes, input]; keep only the input (last child) so the
    // search/placeholder plumbing stays intact while badges are replaced.
    const input = Array.isArray(children) ? children[children.length - 1] : children;
    return (
      <div className="select-customizable__value-container multi-select-checkbox__all-selected">
        <span className="multi-select-checkbox__all-selected-label">{selectProps.allSelectedLabel}</span>
        {input}
      </div>
    );
  }

  return <div className="select-customizable__value-container">{children}</div>;
};

function MultiSelectWithCheckbox({
  options,
  value,
  onChange,
  placeholder,
  isDisabled = false,
  isLoading = false,
  maxVisibleValues = 1,
  name,
  label,
  required = false,
  selectAllLabel,
  allSelectedLabel,
  ariaLabel,
}: MultiSelectWithCheckboxProps): JSX.Element {
  const hasSelectAll = !!selectAllLabel;

  // Prepend the synthetic option so it is a real `role="option"` row and thus
  // keyboard-reachable (a MenuList header would not be).
  const selectOptions: OptionType[] = hasSelectAll ? [{ value: SELECT_ALL_VALUE, label: selectAllLabel as string }, ...options] : options;

  function handleChange(selected: OptionType[] | null, meta?: MultiSelectChangeMeta): void {
    const selectedList = selected ?? [];
    const togglesSelectAll = selectedList.some((opt) => opt.value === SELECT_ALL_VALUE);

    if (hasSelectAll && togglesSelectAll) {
      // Toggling "select all": if everything is already selected, clear it;
      // otherwise select every real option.
      const isAllSelected = value.length === options.length;
      onChange(isAllSelected ? [] : options);
      return;
    }

    // Strip the sentinel so it never leaks to the consumer, and forward the
    // react-select action metadata (e.g. `clear`) so the consumer can distinguish
    // a clear-✕ from a deliberate deselect-all. Only pass `meta` when present so
    // the call arity is unchanged for consumers that don't read it.
    const cleaned = selectedList.filter((opt) => opt.value !== SELECT_ALL_VALUE);
    if (meta) {
      onChange(cleaned, meta);
    } else {
      onChange(cleaned);
    }
  }

  const components: Record<string, React.ComponentType<any>> = {
    DropdownIndicator: CustomDropdownIndicator,
    MultiValue: LimitedMultiValue,
    Option: CheckboxOption,
    ...(allSelectedLabel && { ValueContainer: AllSelectedValueContainer }),
  };

  return (
    <div className="multi-select-checkbox">
      <SelectCustomizable
        allSelectedLabel={allSelectedLabel}
        aria-label={ariaLabel}
        closeMenuOnSelect={false}
        components={components}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isMulti={true}
        isSearchable={false}
        label={label}
        maxVisibleValues={maxVisibleValues}
        menuPosition="absolute"
        name={name}
        onChange={handleChange as (option: OptionType | OptionType[] | null) => void}
        options={selectOptions}
        placeholder={placeholder}
        required={required}
        value={value}
      />
    </div>
  );
}

export default MultiSelectWithCheckbox;
